import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { confirmAction } from '../../utils/confirm';

function CredentialsModal({ credentials, emailSent, onClose }) {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Login Credentials Generated</h2>
        <div className="credentials-box">
          {emailSent ? (
            <div className="credentials-email-sent">
              Credentials have been emailed to the user. A copy is shown below for your records.
            </div>
          ) : (
            <div className="credentials-email-failed">
              Email could not be sent. Please share these credentials with the user manually.
            </div>
          )}
          <div className="credentials-notice">
            The user will be required to change their password on first login.
          </div>
          <div className="credentials-row">
            <label>Username</label>
            <div className="credentials-value">
              <code>{credentials.username}</code>
              <button className="btn btn-sm btn-outline" onClick={() => copyToClipboard(credentials.username)}>Copy</button>
            </div>
          </div>
          <div className="credentials-row">
            <label>One-Time Password</label>
            <div className="credentials-value">
              <code className="otp-code">{credentials.oneTimePassword}</code>
              <button className="btn btn-sm btn-outline" onClick={() => copyToClipboard(credentials.oneTimePassword)}>Copy</button>
            </div>
          </div>
          <div className="credentials-row">
            <div className="credentials-copy-all">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => copyToClipboard(`Username: ${credentials.username}\nOne-Time Password: ${credentials.oneTimePassword}\n\nPlease login and change your password immediately.`)}
              >
                Copy All Credentials
              </button>
            </div>
          </div>
          <div className="credentials-warning">
            This one-time password will not be shown again. Please copy it now.
          </div>
        </div>
        <div className="form-actions">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function UserModal({ tenantId, user, onClose, onSaved }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    fullName: user?.FullName || '',
    username: user?.Username || '',
    email: user?.Email || '',
    mobileNo: user?.MobileNo || '',
    role: user?.Role || 'InstituteAdmin',
    isActive: user?.IsActive !== undefined ? user.IsActive : true,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim()) {
      toast.error('Name and Email are required');
      return;
    }
    if (!isEdit && !form.username.trim()) {
      toast.error('Username is required');
      return;
    }
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/users/${user.UserID}`, {
          fullName: form.fullName,
          email: form.email,
          mobileNo: form.mobileNo,
          isActive: form.isActive,
        });
        toast.success('User updated successfully');
        onSaved(null);
      } else {
        const response = await api.post('/users', { tenantId, ...form });
        if (response.data.success) {
          toast.success(response.data.message);
          onSaved(response.data.credentials, response.data.emailSent);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Institute User' : 'Add Institute User'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="required-label">Full Name</label>
            <input type="text" placeholder="Enter full name" value={form.fullName} onChange={(e) => handleChange('fullName', e.target.value)} autoFocus />
          </div>
          {!isEdit && (
            <div className="form-group">
              <label className="required-label">Username (for login)</label>
              <input type="text" placeholder="Enter a unique username" value={form.username} onChange={(e) => handleChange('username', e.target.value.toLowerCase().replace(/\s/g, ''))} />
              <span className="form-hint">This will be used for login. Cannot be changed later.</span>
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label className="required-label">Email ID</label>
              <input type="email" placeholder="user@institute.com" value={form.email} onChange={(e) => handleChange('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Mobile No (Optional)</label>
              <input type="text" placeholder="e.g., +91-9876543210" value={form.mobileNo} onChange={(e) => handleChange('mobileNo', e.target.value)} maxLength={20} />
            </div>
          </div>
          <div className="form-group">
            <label>Role</label>
            <input type="text" value="Institute Admin" disabled style={{ background: '#f5f5f5', color: '#666' }} />
            <span className="form-hint">Default role for institute users</span>
          </div>
          {isEdit && (
            <div className="form-group">
              <label>Status</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                <label className="toggle-switch">
                  <input type="checkbox" checked={form.isActive} onChange={(e) => handleChange('isActive', e.target.checked)} />
                  <span className="toggle-slider"></span>
                </label>
                <span style={{ fontSize: 14, color: form.isActive ? '#2e7d32' : '#c62828' }}>
                  {form.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          )}
          <div className="form-actions">
            <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={saving}>
              <span>💾</span> {saving ? 'Saving...' : isEdit ? 'Update User' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InstituteUsers({ tenantId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [showCredentials, setShowCredentials] = useState(null);
  const [credentialsEmailSent, setCredentialsEmailSent] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/users/${tenantId}`);
      if (response.data.success) setUsers(response.data.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleSaved = (credentials, emailSent) => {
    setShowModal(false);
    setEditUser(null);
    loadUsers();
    if (credentials) {
      setShowCredentials(credentials);
      setCredentialsEmailSent(!!emailSent);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!(await confirmAction('Reset Password?', 'This will generate a new one-time password for this user.', { confirmText: 'Yes, reset', confirmColor: '#e65100', icon: 'question' }))) return;
    try {
      const response = await api.post(`/users/${userId}/reset-password`);
      if (response.data.success) {
        toast.success(response.data.message);
        setShowCredentials(response.data.credentials);
        setCredentialsEmailSent(!!response.data.emailSent);
      }
    } catch {
      toast.error('Failed to reset password');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>Institute Users</h3>
        <button className="btn btn-add" onClick={() => { setEditUser(null); setShowModal(true); }}>
          + Add User
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No institute users found. Click "Add User" to create an institute admin.
        </div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>#</th>
              <th>Full Name</th>
              <th>Username</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Role</th>
              <th>Status</th>
              <th>Password</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u, idx) => (
              <tr key={u.UserID}>
                <td>{idx + 1}</td>
                <td style={{ fontWeight: 500 }}>{u.FullName}</td>
                <td><code style={{ background: '#f0f0f0', padding: '2px 8px', borderRadius: 4, fontSize: 13 }}>{u.Username}</code></td>
                <td>{u.Email}</td>
                <td>{u.MobileNo || '-'}</td>
                <td><span className="badge badge-provider">{u.Role}</span></td>
                <td>
                  <span className={`badge ${u.IsActive ? 'badge-active' : 'badge-inactive'}`}>
                    {u.IsActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  {u.MustChangePassword ? (
                    <span className="badge badge-warning">OTP Pending</span>
                  ) : (
                    <span className="badge badge-active">Set</span>
                  )}
                </td>
                <td>{formatDate(u.CreatedAt)}</td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn action-edit" title="Edit" onClick={() => { setEditUser(u); setShowModal(true); }}>✏</button>
                    <button className="action-btn action-reset" title="Reset Password" onClick={() => handleResetPassword(u.UserID)}>🔑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <UserModal
          tenantId={tenantId}
          user={editUser}
          onClose={() => { setShowModal(false); setEditUser(null); }}
          onSaved={handleSaved}
        />
      )}

      {showCredentials && (
        <CredentialsModal
          credentials={showCredentials}
          emailSent={credentialsEmailSent}
          onClose={() => { setShowCredentials(null); setCredentialsEmailSent(false); }}
        />
      )}
    </>
  );
}

export default InstituteUsers;
